<template>
  <div
    v-if="character"
    class="text-center bb-tooltip-wrapper summary-section"
  >
    <p class="bb-hdg mt-2 mb-3">General</p>
    <div class="row mb-2"></div>
    <div class="row mb-2">
      <ToolTip
        :text="displayKBText()"
        position="bottom"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="character.know_bonus_percent"
            id="kb"
            disabled
          />
          <label
            class="readonly bb-float-label optional"
            for="kb"
            >Know Bonus
            <span class="bb-orange-circle"></span>
          </label>
        </div>
      </ToolTip>
    </div>
    <div class="row mb-2">
      <ToolTip
        :text="displayMPText()"
        position="bottom"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="character.mp"
            id="mp"
            disabled
          />
          <label
            class="readonly bb-float-label optional"
            for="mp"
            >Total Magic Pts
            <span class="bb-orange-circle"></span>
          </label>
        </div>
      </ToolTip>
    </div>
    <div class="row mb-2">
      <div class="form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center text-uppercase"
            :value="dex_pen_armour_display"
            id="dex_pen"
            disabled
          />
          <label
            class="readonly bb-float-label optional"
            for="dex_pen"
            >Armour Dex Pen %</label
          >
        </div>
      </div>
    </div>
    <div class="row mb-2">
      <ToolTip
        :text="displayMaxEncText()"
        position="bottom"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="formatEnc(max_enc)"
            id="max_enc"
            disabled
          />
          <label
            class="readonly bb-float-label optional"
            for="max_enc"
            >Max Enc
            <span class="bb-orange-circle"></span>
          </label>
        </div>
      </ToolTip>
    </div>
    <div class="row mb-2">
      <div class="form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center text-uppercase"
            :value="formatEnc(cur_enc)"
            id="cur_enc"
            disabled
          />
          <label
            class="readonly bb-float-label optional"
            for="cur_enc"
            >Cur Enc</label
          >
        </div>
      </div>
    </div>
    <div class="row mb-2">
      <ToolTip
        :text="displayAvailEncText()"
        position="bottom"
      >
        <div class="form-group">
          <div class="form-floating">
            <input
              readonly
              :class="[
                'form-control text-center text-uppercase',
                { 'bb-enc-overload': available_enc < 0 },
              ]"
              :value="formatEnc(available_enc)"
              id="available_enc"
              disabled
            />
            <label
              class="readonly bb-float-label optional"
              for="available_enc"
              >Avail Enc
              <span class="bb-orange-circle"></span>
            </label>
          </div>
        </div>
      </ToolTip>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'

import ToolTip from '../components/ToolTip.vue'
import getCharacterArmourDexPen from '../helpers/getCharacterArmourDexPen'

export default {
  name: 'SheetSummaryGeneral',
  components: {
    ToolTip,
  },
  props: {
    character: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const global = inject('global')
    return { global }
  },
  computed: {
    dex_pen_armour_display() {
      return getCharacterArmourDexPen(this.character || {})
    },
    cur_enc() {
      const items = this.character?.items || []
      let total = 0

      items.forEach((item) => {
        const encPerItem = parseFloat(item?.enc || 0)
        const qty = parseInt(item?.tally_qty || 0, 10)

        if (!Number.isNaN(encPerItem) && !Number.isNaN(qty)) {
          total += encPerItem * qty
        }
      })

      return Math.round((total + Number.EPSILON) * 100) / 100
    },
    available_enc() {
      const max = this.max_enc || 0
      const cur = this.cur_enc || 0
      return Math.round((max - cur + Number.EPSILON) * 100) / 100
    },
    max_enc() {
      return this.calculateMaxEnc(this.character)
    },
  },
  methods: {
    calculateMaxEnc(character) {
      const str = Number(character?.str_cur ?? 0)
      const siz = Number(character?.siz_cur ?? 0)
      const safeStr = Number.isNaN(str) ? 0 : str
      const safeSiz = Number.isNaN(siz) ? 0 : siz
      return safeStr + 2 * safeSiz
    },
    formatEnc(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return ''
      }
      if (this.global && typeof this.global.twoDecimal === 'function') {
        return this.global.twoDecimal(num)
      }
      return num.toFixed(2)
    },
    displayKBText() {
      return `Int (${this.character.int_cur}) - 10`
    },
    displayMPText() {
      return `Pow (${this.character.pow_cur})`
    },
    displayMaxEncText() {
      return `House Rule:\nStr (${this.character.str_cur}) + 2 x Siz (${this.character.siz_cur})`
    },
    displayAvailEncText() {
      return `Max Enc (${this.formatEnc(this.max_enc)} - Cur Enc ${this.formatEnc(this.cur_enc)})`
    },
  },
}
</script>

<style scoped>
.summary-section {
  min-width: 0;
  width: 100%;
}

.bb-enc-overload {
  background-color: rgb(198, 4, 4) !important;
  color: #ffffff !important;
}
</style>
